import { ScreenBase } from "@frui.ts/screens";
import { bound } from "@frui.ts/helpers";
import { action, computed, observable } from "mobx";
import LocalizationService from "services/localizationService";
import ChargeOrderService from "services/chargeOrderService";
import ChargeTypesService from "services/chargeTypesService";
import { interfaces } from "inversify";
import GDPRViewModel from "./gdpr/GDPRViewModel";
import { ManualEntityValidator } from "@frui.ts/validation";
import ChargeOrderContactItem from "models/chargeOrderContactItem";
import NotificationPreferences from "models/notificationPreferences";
import { setVisitedProperty } from "utils/validationPatch";
import ChargeDetailResponse from "../../entities/chargeDetailResponse";
import ChargeValidity from "entities/chargeValidity";

export default class ContactViewModel extends ScreenBase {
  private unsubscriptions: Array<() => void> = [];
  protected manualValidator = new ManualEntityValidator<NotificationPreferences>(false);
  gdprViewModel: GDPRViewModel;
  @observable formElement?: HTMLFormElement;
  @observable phoneNumberChanged = false;
  @observable storedEmailNotification?: string;
  @observable storedSmsNotification?: string;

  constructor(
    public chargeOrderContactItem: ChargeOrderContactItem | ChargeDetailResponse,
    private chargeOrderService: ChargeOrderService,
    private chargeService: ChargeTypesService,
    public localizationService: LocalizationService,
    private gdprVMFactory: ReturnType<typeof GDPRViewModel.Factory>,
    public isSingleEshop = false,
    public isMultiEshop = false
  ) {
    super();
    this.gdprViewModel = this.gdprVMFactory(this.GDPR);
  }
  protected onInitialize() {
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get defaultPrefixNumber() {
    if (!this.phoneNumberChanged) {
      return this.localizationService.defaultPrefixNumber;
    }
    return undefined;
  }

  @computed
  get notificationEnabled() {
    return this.notificationPreferences._notificationEnabled;
  }

  set notificationEnabled(notificationEnabled) {
    this.notificationPreferences._notificationEnabled = notificationEnabled;
    if (!notificationEnabled) {
      // Disable all notification selections and store it for later.
      this.storedEmailNotification = this.notificationPreferences._emailAddressNotification;
      this.notificationPreferences._emailAddressNotification = undefined;
      this.notificationPreferences.emailNotificationEnabled = false;

      this.storedSmsNotification = this.notificationPreferences.phoneNumber;
      this.notificationPreferences.phoneNumber = undefined;
      this.notificationPreferences.smsNotificationEnabled = false;
    } else {
      // Enable all notification selections restore the original values.
      this.notificationPreferences._emailAddressNotification = this.storedEmailNotification;
      this.notificationPreferences.phoneNumber = this.storedSmsNotification;
      this.notificationPreferences.emailNotificationEnabled = this.isEmailNotificationSet;
      this.notificationPreferences.smsNotificationEnabled = this.isPhoneNumberSet;
    }
  }

  @computed
  get smsNotificationEnabled() {
    return this.notificationPreferences.smsNotificationEnabled;
  }

  @computed
  get isNotificationEmailSame() {
    return this.notificationPreferences._isNotificationEmailSame ?? false;
  }

  @computed
  get GDPR() {
    return this.translate("gdpr.description", true);
  }

  @computed
  get notificationPreferences() {
    return this.chargeOrderContactItem.notificationPreferences;
  }

  @computed
  get showNotificationPreferences() {
    const charges = this.chargeOrderService.chargeOrderItems;
    if (this.chargeOrderService.chargeOrder._origin === "multiple") {
      return !charges.find(
        charge => this.chargeService.findChargeType(charge.chargeTypeId)?.validFor === ChargeValidity.SingleDay
      );
    }
    return !charges.find(charge => charge._singleChargeType);
  }

  @computed
  get notificationEnabledInfo() {
    if (this.chargeOrderService.chargeOrder._origin === "multiple") {
      return this.chargeOrderService.notificationInfoMulti();
    }
    return this.chargeOrderService.notificationInfoSingle();
  }

  get isEmailNotificationSet() {
    return (
      this.notificationPreferences._emailAddressNotification !== undefined &&
      this.notificationPreferences._emailAddressNotification.length > 0
    );
  }

  get isPhoneNumberSet() {
    return this.notificationPreferences.phoneNumber !== undefined && this.notificationPreferences.phoneNumber.length > 0;
  }

  @bound
  onEmailAddressNotificationChanged() {
    setVisitedProperty(this.notificationPreferences, "_emailAddressNotification");
    this.notificationPreferences.emailNotificationEnabled = this.isEmailNotificationSet;
  }

  @bound
  onPhoneNumberChanged() {
    this.phoneNumberChanged = true;
    setVisitedProperty(this.notificationPreferences, "phoneNumber");
    this.notificationPreferences.smsNotificationEnabled = this.isPhoneNumberSet;
  }

  @action.bound
  translate(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`eshop.order.contact.${code}`, undefined, disableUnbreakableRules);
  }

  @action.bound
  translateMulti(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`multiEshop.${code}`, undefined, disableUnbreakableRules);
  }

  static Factory({ container }: interfaces.Context) {
    return (
      chargeOrderContactItem: ChargeOrderContactItem | ChargeDetailResponse,
      isSingleEshop = false,
      isMultiEshop = false
    ) => {
      return new ContactViewModel(
        chargeOrderContactItem,
        container.get(ChargeOrderService),
        container.get(ChargeTypesService),
        container.get(LocalizationService),
        container.get(GDPRViewModel.Factory),
        isSingleEshop,
        isMultiEshop
      );
    };
  }
}
